import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ConditionalsTable.module.css';
import ConditionsTip from './ConditionsTip';
import SeeMoreHoverCard from '../../ui/hover-card/SeeMoreHoverCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUpRightAndDownLeftFromCenter,
    faDownLeftAndUpRightToCenter,
} from '@fortawesome/free-solid-svg-icons';

const ConditionalsTable: React.FC<{
    style?: 'info' | 'default' | 'critical' | 'department';
    sizes?: number[];
    title?: string;
    headers: (string | ReactNode)[];
    children?: ReactNode;
    fields?: (string | ReactElement)[][];
    hasSeeMore?: boolean;
    expandable?: boolean;
    isInfo?: boolean;
}> = ({
    headers,
    title,
    children,
    fields,
    sizes,
    style = 'default',
    hasSeeMore = true,
    expandable = false,
    isInfo,
}) => {
    const { t } = useTranslation();
    const sizeSum =
        sizes !== undefined ? sizes.reduce((a, c) => a + c, 0) : headers.length;

    const [expanded, setExpanded] = useState(false);

    const width = (i: number) => {
        // Default to even width
        const s = sizes !== undefined ? sizes[i] : sizeSum;
        return {
            width: s * (100 / sizeSum) + '%',
        };
    };

    const [showReadMore, setShowReadMore] = useState(false);
    const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());

    const pRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        if (pRef.current) {
            const lineHeight = parseInt(
                window.getComputedStyle(pRef.current).lineHeight
            );
            const height = pRef.current.offsetHeight;

            setShowReadMore(height > 4 * lineHeight);
        }
    }, []);

    const toggleExpanded = (cellKey: string) => {
        setExpandedCells((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(cellKey)) {
                newSet.delete(cellKey);
            } else {
                newSet.add(cellKey);
            }
            return newSet;
        });
    };

    return (
        <div className={styles[style]}>
            <div className={styles.wrapper}>
                <div className={styles.headers}>
                    {headers
                        .filter((h) => h !== 'Condition' || !expanded)
                        .map((h, index) => (
                            <div
                                className={styles.headerCell}
                                style={width(index)}
                                key={h?.toString()}
                            >
                                {h}
                            </div>
                        ))}
                    {expandable && (
                        <button
                            className={styles.expandButton}
                            onClick={() => setExpanded(!expanded)}
                        >
                            <FontAwesomeIcon
                                icon={
                                    expanded
                                        ? faDownLeftAndUpRightToCenter
                                        : faUpRightAndDownLeftFromCenter
                                }
                            />
                        </button>
                    )}
                </div>
                <div className={styles.fields}>
                    {fields.map((row, index) => {
                        let newRow = row;
                        if (
                            headers[headers.length - 1] === 'Condition' &&
                            expanded
                        ) {
                            newRow = row.slice(0, row.length - 1);
                        }
                        return (
                            <>
                                <div
                                    className={styles.row}
                                    key={newRow
                                        .map((c) => c?.toString())
                                        .join('')}
                                >
                                    {newRow.map((cell, index) => {
                                        if (
                                            hasSeeMore &&
                                            row.length > 1 &&
                                            index === row.length - 1 &&
                                            cell?.toString().length >
                                                100 / row.length
                                        ) {
                                            return (
                                                <div
                                                    className={styles.cell}
                                                    style={width(index)}
                                                    key={cell?.toString()}
                                                >
                                                    {/* <SeeMoreHoverCard
                                                    title={headers[index] as string}
                                                    content={{
                                                        [headers[index] as string]:
                                                            cell,
                                                    }}
                                                /> */}
                                                    <SeeMoreHoverCard
                                                        title={title}
                                                        content={row.reduce<object>(
                                                            (obj, c, idx) => {
                                                                return {
                                                                    ...obj,
                                                                    [(
                                                                        headers as string[]
                                                                    )[idx]]: c,
                                                                };
                                                            },
                                                            {}
                                                        )}
                                                    />
                                                    {/* <ConditionsTip
                                                    placeholder="See more"
                                                    title={title}
                                                    content={row.reduce<object>(
                                                        (obj, c, idx) => {
                                                            return {
                                                                ...obj,
                                                                [(
                                                                    headers as string[]
                                                                )[idx]]: c,
                                                            };
                                                        },
                                                        {}
                                                    )}
                                                /> */}
                                                </div>
                                            );
                                        } else if (typeof cell === 'string') {
                                            return (
                                                <div
                                                    className={styles.cell}
                                                    style={width(index)}
                                                    dangerouslySetInnerHTML={{
                                                        __html: cell,
                                                    }}
                                                ></div>
                                            );
                                        } else {
                                            const cellKey = `${row
                                                .map((c) => c?.toString())
                                                .join('')}-${index}`;
                                            const isExpanded =
                                                expandedCells.has(cellKey);

                                            return (
                                                <div
                                                    className={styles.cell}
                                                    style={width(index)}
                                                >
                                                    <div
                                                        className={
                                                            showReadMore &&
                                                            !isExpanded
                                                                ? styles.collapsedTextWrapper
                                                                : styles.expandedTextWrapper
                                                        }
                                                    >
                                                        <p ref={pRef}>{cell}</p>
                                                    </div>
                                                    {isInfo && showReadMore && (
                                                        <button
                                                            className={
                                                                styles.toggleButton
                                                            }
                                                            onClick={() =>
                                                                toggleExpanded(
                                                                    cellKey
                                                                )
                                                            }
                                                        >
                                                            {isExpanded
                                                                ? t(
                                                                      'conditionalsTable.buttons.readLess'
                                                                  )
                                                                : t(
                                                                      'conditionalsTable.buttons.readMore'
                                                                  )}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        }
                                    })}
                                </div>
                                {headers[headers.length - 1] === 'Condition' &&
                                    expanded &&  (
                                        <div
                                            className={`${styles.row} ${styles.cell}`}
                                        >
                                            {row[row.length - 1]?.toString()}
                                        </div>
                                    )}
                            </>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ConditionalsTable;
